import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";

type ThemeContextValue = {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  setMode(mode: ThemeMode): void;
  toggleTheme(): void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const storageKey = "qualtio-workhub-theme";

export function ThemeProvider({
  children,
  defaultMode = "system",
}: {
  children: ReactNode;
  defaultMode?: ThemeMode;
}) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode() ?? defaultMode);
  const [systemMode, setSystemMode] = useState<"light" | "dark">(() => getSystemMode());
  const resolvedMode = mode === "system" ? systemMode : mode;

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemMode(media.matches ? "dark" : "light");
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset["theme"] = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedMode,
      setMode(nextMode) {
        setModeState(nextMode);
        localStorage.setItem(storageKey, nextMode);
      },
      toggleTheme() {
        const nextMode = resolvedMode === "light" ? "dark" : "light";
        setModeState(nextMode);
        localStorage.setItem(storageKey, nextMode);
      },
    }),
    [mode, resolvedMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error("useTheme must be used within ThemeProvider");
  }

  return context;
}

function readStoredMode(): ThemeMode | null {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const value = localStorage.getItem(storageKey);
  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }

  return null;
}

function getSystemMode(): "light" | "dark" {
  if (typeof window === "undefined") {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
